import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getApiBaseUrl, API_ENDPOINTS } from '../config/api';
import './DeclarationsTable.css';

const DEBOUNCE_MS = 350;

/**
 * Admin view: all transactions across all agents (from agent_business).
 * Includes backfilled and every agent-linked cover. Pagination, search, agent and date filters.
 */
const AllTransactionsTable = ({ user, onShowNotification }) => {
  const API_BASE_URL = getApiBaseUrl();
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [agentId, setAgentId] = useState('');
  const [agentSearchText, setAgentSearchText] = useState('');
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [certificateStatus, setCertificateStatus] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [agents, setAgents] = useState([]);
  const agentDropdownRef = useRef(null);
  const abortRef = useRef(null);
  const [certDownloadingNumber, setCertDownloadingNumber] = useState(null);

  useEffect(() => {
    loadAgents();
  }, []);

  // Debounce search so we don't refetch on every keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchApplied(searchInput.trim());
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    loadTransactions();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [page, searchApplied, agentId, startDate, endDate, certificateStatus]);

  // Realtime: poll for certificate status updates when tab is visible (e.g. issued/pending_confirmation)
  const POLL_INTERVAL_MS = 60000;
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadTransactions(true);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [page, searchApplied, agentId, startDate, endDate, certificateStatus]);

  // Refetch when user returns to tab so certificate status updates show immediately
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadTransactions(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [page, searchApplied, agentId, startDate, endDate, certificateStatus]);

  // Close agent dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (agentDropdownRef.current && !agentDropdownRef.current.contains(e.target)) {
        setShowAgentDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAgents = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/admin/agents?limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.agents) setAgents(data.agents);
      }
    } catch (e) {
      console.error('Error loading agents:', e);
    }
  };

  const loadTransactions = async (silent = false) => {
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    if (!silent) setIsLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (searchApplied) params.set('search', searchApplied);
      if (agentId) params.set('agentId', String(agentId));
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (certificateStatus) params.set('certificateStatus', certificateStatus);
      const res = await fetch(`${API_BASE_URL}/api/admin/transactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: ac.signal
      });
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          onShowNotification?.('Session expired or access denied.', 'warning');
          return;
        }
        throw new Error(res.statusText);
      }
      const data = await res.json();
      if (data.success && data.data) {
        setTransactions(data.data.transactions || []);
        setPagination(data.data.pagination || { page: 1, limit: 25, total: 0, totalPages: 1 });
      }
    } catch (e) {
      if (e?.name === 'AbortError') return;
      if (!silent) {
        console.error('Error loading transactions:', e);
        onShowNotification?.(e.message || 'Failed to load transactions', 'error');
        setTransactions([]);
      }
    } finally {
      if (!silent && abortRef.current === ac) setIsLoading(false);
    }
  };

  const downloadCertificate = async (certificateNumber) => {
    if (!certificateNumber || certDownloadingNumber) return;
    setCertDownloadingNumber(certificateNumber);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(API_ENDPOINTS.getCertificateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ certificateNumber: String(certificateNumber).trim() })
      });
      const result = await res.json().catch(() => ({}));
      if (result.success && result.certificateUrl) {
        const link = document.createElement('a');
        link.href = result.certificateUrl;
        link.download = `Certificate_${certificateNumber}.pdf`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        onShowNotification?.('Certificate download started.', 'success');
      } else {
        onShowNotification?.(result.error || 'Could not get certificate download link.', 'error');
      }
    } catch (err) {
      onShowNotification?.(err.message || 'Download failed.', 'error');
    } finally {
      setCertDownloadingNumber(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatAmount = (n) => {
    if (n == null || isNaN(Number(n))) return '—';
    return Number(n).toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  // Filter agents by name in realtime (agency_name, short_description, ira_number)
  const agentSearchLower = (agentSearchText || '').trim().toLowerCase();
  const filteredAgents = useMemo(() => {
    if (!agentSearchLower) return (agents || []).slice(0, 100);
    return (agents || []).filter((a) => {
      const name = (a.agency_name || '').toLowerCase();
      const short = (a.short_description || '').toLowerCase();
      const ira = (a.ira_number || '').toLowerCase();
      return name.includes(agentSearchLower) || short.includes(agentSearchLower) || ira.includes(agentSearchLower);
    }).slice(0, 50);
  }, [agents, agentSearchLower]);

  const selectedAgent = useMemo(() => agents.find((a) => String(a.id) === String(agentId)), [agents, agentId]);
  const agentDisplayName = selectedAgent ? (selectedAgent.agency_name || selectedAgent.short_description || `Agent ${agentId}`) : '';

  const clearFilters = () => {
    setSearchInput('');
    setSearchApplied('');
    setAgentId('');
    setAgentSearchText('');
    setStartDate('');
    setEndDate('');
    setCertificateStatus('');
    setPage(1);
    setShowAgentDropdown(false);
  };

  const hasActiveFilters = searchApplied || agentId || startDate || endDate || certificateStatus;

  return (
    <div className="declarations-table-container all-transactions-page">
      <div className="declarations-header all-transactions-header">
        <h2>All Transactions (All Agents)</h2>
        <p className="declarations-subtitle">Every agent-linked cover from agent_business (includes backfilled). Dashboard production totals use completed payments, which can differ. Search by registration, certificate number, client name, client ID, policy, or payment reference.</p>
      </div>

      <div className="declarations-filters all-transactions-filters">
        <div className="all-transactions-filter-group all-transactions-search-group">
          <label htmlFor="all-tx-search" className="all-transactions-filter-label">Search</label>
          <input
            id="all-tx-search"
            type="text"
            placeholder="Registration, certificate, client, policy…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="declarations-search all-transactions-search-input"
            aria-label="Search by registration, certificate number, client name, client ID or policy"
          />
        </div>
        <div className="all-transactions-filter-group all-transactions-agent-wrap" ref={agentDropdownRef}>
          <label htmlFor="all-tx-agent" className="all-transactions-filter-label">Agent</label>
          <div className="all-transactions-agent-filter">
            <input
              id="all-tx-agent"
              type="text"
              placeholder="Type to search agent..."
              value={agentId ? agentDisplayName : agentSearchText}
              onChange={(e) => {
                setAgentSearchText(e.target.value);
                setAgentId('');
                setShowAgentDropdown(true);
              }}
              onFocus={() => setShowAgentDropdown(true)}
              className="declarations-search all-transactions-agent-input"
              aria-label="Filter by agent name"
              aria-haspopup="listbox"
              aria-expanded={showAgentDropdown}
              autoComplete="off"
            />
            {agentId && (
              <button
                type="button"
                className="all-transactions-clear-agent"
                onClick={() => { setAgentId(''); setAgentSearchText(''); setPage(1); }}
                aria-label="Clear agent filter"
              >
                ✕
              </button>
            )}
            {showAgentDropdown && (
              <ul className="all-transactions-agent-dropdown" role="listbox" id="all-tx-agent-listbox">
                {!agentSearchLower && (
                  <li
                    role="option"
                    tabIndex={0}
                    onClick={() => { setAgentId(''); setAgentSearchText(''); setShowAgentDropdown(false); setPage(1); }}
                    onKeyDown={(e) => e.key === 'Enter' && (setAgentId(''), setAgentSearchText(''), setShowAgentDropdown(false), setPage(1))}
                  >
                    All agents
                  </li>
                )}
                {filteredAgents.length === 0 ? (
                  <li className="all-transactions-agent-empty">No agents match</li>
                ) : (
                  filteredAgents.map((a) => (
                    <li
                      key={a.id}
                      role="option"
                      tabIndex={0}
                      onClick={() => {
                        setAgentId(String(a.id));
                        setAgentSearchText('');
                        setShowAgentDropdown(false);
                        setPage(1);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setAgentId(String(a.id));
                          setAgentSearchText('');
                          setShowAgentDropdown(false);
                          setPage(1);
                        }
                      }}
                    >
                      {a.agency_name || a.short_description || `Agent ${a.id}`}
                      {a.short_description && a.agency_name !== a.short_description ? ` (${a.short_description})` : ''}
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
        </div>
        <div className="all-transactions-filter-group all-transactions-date-group">
          <label htmlFor="all-tx-from" className="all-transactions-filter-label">From date</label>
          <input
            id="all-tx-from"
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            className="declarations-date-input all-transactions-date-input"
            aria-label="From date"
          />
        </div>
        <div className="all-transactions-filter-group all-transactions-date-group">
          <label htmlFor="all-tx-to" className="all-transactions-filter-label">To date</label>
          <input
            id="all-tx-to"
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            className="declarations-date-input all-transactions-date-input"
            aria-label="To date"
          />
        </div>
        <div className="all-transactions-filter-group">
          <label htmlFor="all-tx-cert-status" className="all-transactions-filter-label">Certificate</label>
          <select
            id="all-tx-cert-status"
            value={certificateStatus}
            onChange={(e) => { setCertificateStatus(e.target.value); setPage(1); }}
            className="declarations-search all-transactions-date-input"
            aria-label="Filter by certificate status"
          >
            <option value="">All</option>
            <option value="issued">Issued</option>
            <option value="pending">Pending</option>
            <option value="pending_confirmation">Pending confirmation</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        {hasActiveFilters && (
          <div className="all-transactions-filter-group all-transactions-clear-wrap">
            <span className="all-transactions-filter-label all-transactions-filter-label-invisible">Actions</span>
            <button type="button" className="all-transactions-clear-filters" onClick={clearFilters}>
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="declarations-loading">Loading transactions...</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="declarations-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Agency</th>
                  <th>Client ID</th>
                  <th>Policy</th>
                  <th>Registration</th>
                  <th>Cover</th>
                  <th>Vehicle class</th>
                  <th>Insured</th>
                  <th>Premium (KES)</th>
                  <th>Payment</th>
                  <th>Certificate</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr><td colSpan={11}>No transactions found.</td></tr>
                ) : (
                  transactions.map((t) => (
                    <tr key={t.id}>
                      <td>{formatDate(t.transaction_date)}</td>
                      <td>{t.agency_name || t.short_description || `Agent ${t.agent_id}`}</td>
                      <td>{t.client_id || '—'}</td>
                      <td>{t.policy_number || '—'}</td>
                      <td>{t.vehicle_registration || t.registration_number || '—'}</td>
                      <td>{t.cover_type || '—'}</td>
                      <td>{t.vehicle_class || '—'}</td>
                      <td>{(t.insured_name || '').slice(0, 30)}{(t.insured_name && t.insured_name.length > 30) ? '…' : ''}</td>
                      <td>{formatAmount(t.premium_amount)}</td>
                      <td><span className={`status-badge status-${(t.payment_status || '').toLowerCase()}`}>{t.payment_status || '—'}</span></td>
                      <td>
                        {t.certificate_number ? (
                          <span className="cert-cell-with-download">
                            <span style={{ color: '#27ae60', fontWeight: 'bold' }}>✅ {t.certificate_number}</span>
                            <button
                              type="button"
                              className="btn-download-cert"
                              disabled={certDownloadingNumber === t.certificate_number}
                              onClick={() => downloadCertificate(t.certificate_number)}
                              title="Download certificate PDF"
                            >
                              {certDownloadingNumber === t.certificate_number ? '…' : 'Download'}
                            </button>
                          </span>
                        ) : (
                          <span className={`status-badge status-${(t.certificate_status || '').toLowerCase().replace(/\s/g, '-')}`}>{t.certificate_status || '—'}</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="declarations-pagination">
            <span>Total: {pagination.total} · Page {pagination.page} of {pagination.totalPages || 1}</span>
            <div>
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</button>
              <button type="button" disabled={page >= (pagination.totalPages || 1)} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AllTransactionsTable;
